import { FRAIS_CAT } from '../constants/accounting';
import { tvaFactureFournisseur } from './costs';
import { piecesDesDepenses } from './pieces-depenses';

/** La date du jour, au format `JJ.MM.AAAA`. */
const dateDuJour = (d) =>
  String(d.getDate()).padStart(2, '0') +
  '.' +
  String(d.getMonth() + 1).padStart(2, '0') +
  '.' +
  d.getFullYear();
import { brutDuMois } from './salaires-periode';
import { tvaDeLaDepense } from './vat';
import { vehiculesDeLAnnee } from './vehicules';
import { nomComplet } from './acces';
import { sansPaiementsEtat } from './paiements-etat';
import { datesCouvertes, enParts } from './depenses-reparties';
import { periodesComptable } from './cloture';

/**
 * Toutes les dépenses de l'entreprise, hors TVA récupérable, une ligne par
 * pièce ou par salaire du mois.
 *
 * <p>L'accueil n'additionnait que l'onglet Frais : une colle payée sur un
 * chantier, les heures d'un employé, rien n'y figurait, et le patron lisait
 * « Dépenses 0.00 · Marge 100 % ». Sont comptés ici :
 * <ul>
 *   <li>les dépenses (onglet Frais), moins la TVA qui se récupère ; une
 *       dépense répartie sur les mois qu'elle couvre (`moisCouverts`) donne
 *       une ligne d'argent sorti à sa date et une ligne de charge par mois
 *       (`reparti` : le paiement d'où elle vient) ;</li>
 *   <li>les factures des fournisseurs saisies sur les chantiers, une seule
 *       fois quand la même dépense est aussi dans Frais (même chantier, même
 *       montant, même jour — la règle de la position TVA du serveur) ;</li>
 *   <li>les sous-traitants des chantiers, au prix TTC de leur facture ;</li>
 *   <li>la marchandise et la sous-traitance jointes aux offres et factures,
 *       même payées, au taux normal, une seule fois quand le même achat est
 *       déjà saisi (même montant, même jour) ;</li>
 *   <li>les salaires bruts du mois (heures × taux, sinon salaire mensuel),
 *       charges patronales comprises, datés du 1er du mois.</li>
 * </ul>
 *
 * @param {object} p pièces de la société
 * @param {object[]} [p.frais] dépenses
 * @param {object[]} [p.chantiers] chantiers, leurs factures fournisseurs et pointages
 * @param {object[]} [p.achatsJoints] pièces chiffrées des offres et factures
 *   (`{piece}` ou la pièce seule), dont la date est au format `AAAA-MM-JJ` ;
 *   `document` (« facture 07 ») dit d'où vient la pièce
 * @param {object[]} [p.employes] fiches du personnel
 * @param {object} [p.soc] société
 * @param {number} [p.chargesSociales] part patronale, en fraction du brut
 * @param {(e: object) => boolean} [p.exclu] fiche à ne pas compter (le patron indépendant)
 * @param {Date} [p.maintenant] date de référence
 * @param {number} [p.annees] nombre d'années de salaires à compter, l'actuelle comprise
 * @param {boolean} [p.lppDansLesSalaires] la LPP est comptée dans les charges
 *   des salaires : les factures de la caisse de pension ne coûtent pas deux fois
 * @param {boolean} [p.salairesEstimes] les charges des salaires sont estimées :
 *   la facture de la caisse AVS des employés ne coûte pas deux fois
 * @param {object[]} [p.vehicules] fiches des véhicules : quand elles sont
 *   données, leasing et amortissement se comptent par la fiche, un douzième
 *   par mois, comme la comptabilité ; l'achat d'une voiture et les paiements
 *   du leasing saisis en dépense ne coûtent plus rien (ils restent sortis du
 *   compte, pour la trésorerie)
 * @param {number} [p.comptableAnnuel] honoraires annuels du comptable, un
 *   douzième par mois, comme la comptabilité
 * @param {{montant?: number|string, freq?: string, paye?: object}} [p.comptable] la
 *   fiche du comptable : chaque période pointée « payée » (à la main ou par
 *   le relevé) est sortie du compte
 * @param {number} [p.comptableDepuis] première année où le comptable compte
 *   (premiereAnneeDuComptable)
 * @returns {Array<{date: string, montant: number, ttc: number, genre: string, qui: string, ou?: string}>}
 *   les dépenses : `montant` hors TVA récupérable (le coût), `ttc` ce qui est
 *   sorti du compte, `qui` le fournisseur ou l'employé, `ou` le chantier ou le
 *   document d'où vient la pièce
 */
export function depensesHt({
  frais,
  chantiers,
  achatsJoints,
  factures,
  devis,
  employes,
  soc,
  chargesSociales = 0,
  exclu,
  maintenant = new Date(),
  annees = 3,
  vehicules,
  comptableAnnuel = 0,
  comptable,
  comptableDepuis = 0,
  lppDansLesSalaires = false,
  salairesEstimes = false,
}) {
  const lignes = [];
  // La règle de la comptabilité : la voiture achetée et le leasing se
  // comptent par la fiche du véhicule, pas par leurs paiements.
  const avecLeasing = (vehicules || []).some((v) => v.mode === 'LEASING');
  const parLaFiche = (type) =>
    !!vehicules && (type === 'achat_vehicule' || (avecLeasing && type === 'leasing'));
  // Les paiements à l'État (TVA, AVS, impôts) ne sont pas des dépenses.
  sansPaiementsEtat(frais).forEach((x) => {
    const montant = +x.montant || 0;
    // La LPP est déjà estimée dans les charges des salaires : la facture de
    // la caisse de pension sort du compte, mais ne coûte pas une seconde
    // fois (le bénéfice baissait de 10'800.– pour 3'600.– de LPP patronale).
    const dejaEstimee =
      (lppDansLesSalaires && x.type === 'lpp') ||
      (salairesEstimes && x.type === 'charges_sociales');
    const cout =
      parLaFiche(x.type) || dejaEstimee ? 0 : montant - tvaDeLaDepense(x, FRAIS_CAT, soc);
    const ligne = {
      genre: 'frais',
      type: x.type,
      qui:
        String(x.fournisseur || '')
          .split(' — ')[0]
          .trim() ||
        x.libelle ||
        '',
      ou: x.fournisseur && x.libelle ? x.libelle : '',
    };
    // Payée pour plusieurs mois et répartie (l'assurance de l'année payée en
    // janvier) : l'argent sort le jour du paiement, la charge se compte un
    // douzième par mois — comme les véhicules et le comptable plus bas.
    const couverts = cout ? datesCouvertes(x, maintenant) : null;
    if (couverts) {
      lignes.push({ ...ligne, date: x.dateCompta || x.date, montant: 0, ttc: montant });
      enParts(cout, couverts.length).forEach((part, i) =>
        lignes.push({ ...ligne, date: couverts[i], montant: part, ttc: 0, reparti: x.id ?? x }),
      );
    } else {
      lignes.push({ ...ligne, date: x.dateCompta || x.date, montant: cout, ttc: montant });
    }
  });
  // Les factures des fournisseurs et des sous-traitants des chantiers, les
  // achats joints aux offres et factures : les mêmes pièces, chacune une
  // fois, que le classeur et la comptabilité (piecesDesDepenses). Une facture
  // jointe à l'offre et à la facture, saisie au chantier et dans Dépenses,
  // comptait deux fois ; un sous-traitant, à son prix convenu et à sa facture.
  piecesDesDepenses({ frais: sansPaiementsEtat(frais), chantiers, achatsJoints, factures, devis })
    .filter((g) => g.source !== 'frais')
    .forEach((g) => {
      const x = g.item;
      const montant = g.montant;
      const date = g.date || dateDuJour(maintenant);
      const montantTva = g.source === 'soustraitant' ? g.montantTva : x.montantTva;
      lignes.push({
        date,
        montant:
          montant -
          tvaFactureFournisseur(g.source === 'chantier' ? x : { montant, date, montantTva }, soc),
        ttc: montant,
        genre: 'fournisseur',
        sorte: g.source === 'soustraitant' ? 'soustraitant' : g.source,
        qui: g.fournisseur || '',
        ou: g.source === 'joint' ? g.joint?.document || '' : g.chantier?.nom || '',
      });
    });
  // Véhicules et comptable : des charges de l'année, un douzième par mois.
  // L'accueil les ignorait, et son bénéfice dépassait celui de la Compta.
  for (let an = maintenant.getFullYear() - annees + 1; an <= maintenant.getFullYear(); an++) {
    const vehicule = vehicules ? vehiculesDeLAnnee(vehicules, an).charge : 0;
    for (let m = 1; m <= 12; m++) {
      const date = '01.' + String(m).padStart(2, '0') + '.' + an;
      if (vehicule > 0) {
        lignes.push({
          date,
          montant: vehicule / 12,
          ttc: 0,
          genre: 'vehicule',
          qui: 'Véhicules (leasing, amortissement)',
        });
      }
      if (+comptableAnnuel > 0 && an >= comptableDepuis) {
        lignes.push({
          date,
          montant: +comptableAnnuel / 12,
          ttc: 0,
          genre: 'comptable',
          qui: 'Comptable',
        });
      }
    }
  }
  // Les honoraires payés au comptable sont sortis du compte : la charge se
  // compte un douzième par mois (ci-dessus), l'argent le jour de la période
  // payée. « Dépenses avec TVA » et la trésorerie les ignoraient : le
  // « sans TVA » dépassait le « avec TVA » de tout le comptable.
  const DEBUT_PERIODE = { T1: 1, T2: 4, T3: 7, T4: 10, S1: 1, S2: 7, AN: 1 };
  if (comptable && +comptable.montant > 0 && +comptableAnnuel > 0) {
    for (
      let an = Math.max(comptableDepuis, maintenant.getFullYear() - annees + 1);
      an <= maintenant.getFullYear();
      an++
    ) {
      periodesComptable(comptable.freq).forEach(([k]) => {
        if (!(comptable.paye || {})[an + '-' + k]) return;
        const m = DEBUT_PERIODE[k] || +k || 1;
        lignes.push({
          date: '01.' + String(m).padStart(2, '0') + '.' + an,
          montant: 0,
          ttc: +comptable.montant,
          genre: 'comptable',
          paye: true,
          qui: 'Comptable',
        });
      });
    }
  }
  const fin = maintenant.getFullYear() * 12 + maintenant.getMonth();
  const debut = (maintenant.getFullYear() - annees + 1) * 12;
  (employes || [])
    .filter((e) => !(exclu && exclu(e)))
    .forEach((e) => {
      for (let n = debut; n <= fin; n++) {
        const an = Math.floor(n / 12);
        const m = String((n % 12) + 1).padStart(2, '0');
        const brut = brutDuMois(e, chantiers, an + '-' + m, soc);
        if (brut > 0) {
          lignes.push({
            date: '01.' + m + '.' + an,
            montant: brut * (1 + chargesSociales),
            ttc: brut * (1 + chargesSociales),
            genre: 'salaire',
            qui: nomComplet(e) || e.nom || '',
          });
        }
      }
    });
  return lignes;
}

/**
 * Les familles de dépenses de l'accueil, dans l'ordre où elles s'affichent :
 * « combien j'ai payé de sous-traitance, de marchandise, d'essence,
 * d'assurance… ».
 */
export const FAMILLES_DEPENSES = [
  ['soustraitance', '🔨', 'Sous-traitance'],
  ['marchandise', '🧱', 'Marchandise / matériaux'],
  ['joints', '📎', 'Achats joints aux offres et factures'],
  ['salaires', '👷', 'Salaires et charges'],
  ['vehicule', '⛽', 'Essence & véhicule'],
  ['assurance', '🛡', 'Assurances'],
  ['communications', '📱', 'Téléphone & internet'],
  ['locaux', '🏠', 'Loyer & locaux'],
  ['outillage', '🧰', 'Outillage & réparations'],
  ['administratif', '🧾', 'Comptable & administratif'],
  ['autres', '📄', 'Autres frais'],
];

/** La famille d'une dépense de l'onglet Frais, par sa catégorie. */
const FAMILLE_DU_FRAIS = {
  soustraitance: 'soustraitance',
  materiel: 'marchandise',
  port: 'marchandise',
  carburant: 'vehicule',
  vehicule: 'vehicule',
  leasing: 'vehicule',
  achat_vehicule: 'vehicule',
  loc_voiture: 'vehicule',
  stationnement: 'vehicule',
  assurance: 'assurance',
  communications: 'communications',
  loyer: 'locaux',
  location_bail: 'locaux',
  energie: 'locaux',
  menage: 'locaux',
  outillage: 'outillage',
  reparation: 'outillage',
  admin: 'administratif',
  juridique: 'administratif',
  bancaire: 'administratif',
  fournitures: 'administratif',
  interets: 'administratif',
  formation: 'administratif',
};

/**
 * La famille d'une ligne de `depensesHt`.
 *
 * @param {{genre: string, type?: string, sorte?: string}} l ligne de dépense
 * @returns {string} la clé de sa famille (FAMILLES_DEPENSES)
 */
export function familleDeLaDepense(l) {
  if (l.genre === 'salaire') return 'salaires';
  if (l.genre === 'vehicule') return 'vehicule';
  if (l.genre === 'comptable') return 'administratif';
  if (l.genre === 'fournisseur') {
    if (l.sorte === 'soustraitant') return 'soustraitance';
    if (l.sorte === 'joint') return 'joints';
    return 'marchandise';
  }
  return FAMILLE_DU_FRAIS[l.type] || 'autres';
}

/**
 * Les dépenses d'une période, famille par famille : hors TVA récupérable
 * (`montant`) et ce qui est sorti du compte (`ttc`). Les familles vides ne
 * figurent pas.
 *
 * @param {Array<{date: string, montant: number, ttc: number}>} lignes lignes de `depensesHt`
 * @param {(date: string) => boolean} dansLaPeriode dit si une date est dans la période
 * @returns {Array<{cle: string, icone: string, libelle: string, montant: number, ttc: number, lignes: object[]}>}
 *   les familles, dans l'ordre de FAMILLES_DEPENSES, avec leurs pièces : le
 *   patron qui touche « Marchandise » voit sa marchandise, pas tout l'onglet Frais
 */
export function repartitionDepenses(lignes, dansLaPeriode) {
  const parFamille = new Map();
  (lignes || []).forEach((l) => {
    if (!dansLaPeriode(l.date)) return;
    const cle = familleDeLaDepense(l);
    const t = parFamille.get(cle) || { montant: 0, ttc: 0, lignes: [] };
    t.montant += +l.montant || 0;
    t.ttc += +l.ttc || 0;
    if (Math.abs(+l.montant || 0) >= 0.005 || Math.abs(+l.ttc || 0) >= 0.005) t.lignes.push(l);
    parFamille.set(cle, t);
  });
  return FAMILLES_DEPENSES.filter(
    ([cle]) =>
      Math.abs(parFamille.get(cle)?.montant || 0) >= 0.005 ||
      Math.abs(parFamille.get(cle)?.ttc || 0) >= 0.005,
  ).map(([cle, icone, libelle]) => ({ cle, icone, libelle, ...parFamille.get(cle) }));
}

/** « 05.03.2026 » se trie comme « 20260305 ». */
const cleDate = (d) =>
  String(d || '')
    .split('.')
    .reverse()
    .join('');

/**
 * Les pièces d'une famille de dépenses, les plus récentes en haut. Salaires,
 * véhicules et comptable se comptent par mois : une ligne par personne, avec
 * le nombre de mois, plutôt que douze lignes de la même somme.
 *
 * @param {object[]} lignes lignes d'une famille (repartitionDepenses)
 * @returns {Array<{qui?: string, date?: string, ou?: string, montant: number, ttc: number, mois?: number, genre: string}>}
 *   les regroupements par personne d'abord, puis les pièces, de la plus récente à la plus ancienne
 */
export function piecesDeLaFamille(lignes) {
  const parMois = new Map();
  const pieces = [];
  (lignes || []).forEach((l) => {
    // Les mois d'une dépense répartie (l'assurance de l'année, un douzième
    // par mois) se regroupent aussi : une ligne par paiement.
    if (
      l.genre === 'salaire' ||
      l.genre === 'vehicule' ||
      l.genre === 'comptable' ||
      l.reparti != null
    ) {
      const k = l.reparti != null ? l.reparti : l.genre + '|' + (l.qui || '');
      const g = parMois.get(k) || {
        genre: l.genre,
        qui: l.qui,
        mois: 0,
        montant: 0,
        ttc: 0,
        date: l.date,
      };
      g.mois += 1;
      g.montant += +l.montant || 0;
      g.ttc += +l.ttc || 0;
      if (cleDate(l.date) > cleDate(g.date)) g.date = l.date;
      parMois.set(k, g);
    } else {
      pieces.push(l);
    }
  });
  return [
    ...[...parMois.values()].sort((a, b) => b.ttc - a.ttc || b.montant - a.montant),
    ...pieces.sort((a, b) => cleDate(b.date).localeCompare(cleDate(a.date))),
  ];
}
